/**
 * Map Location Popup Component
 * Allows users to pick/adjust their business location on a Google Map
 * and sync it to their GMB profile.
 *
 * @author Visibeen Team - 2024
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  LocationOn,
  CheckCircle,
  ArrowBack,
  MyLocation,
  Search,
  Place,
  Map as MapIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import GMBService from '../../../services/GMBService';
import { loadGoogleMapsApi } from '../../../utils/mapsLoader';
import logger from '../../../utils/logger';

// ============================================
// Styled Components
// ============================================

const HeaderContainer = styled(Box)({
  background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
  color: 'white',
  padding: '32px 24px',
  textAlign: 'center'
});

const IconContainer = styled(Box)({
  width: '70px',
  height: '70px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
  backdropFilter: 'blur(10px)'
});

const ContentContainer = styled(Box)({
  padding: '24px'
});

const MapContainer = styled(Box)({
  width: '100%',
  height: '350px',
  borderRadius: '12px',
  overflow: 'hidden',
  border: '2px solid #e0e0e0',
  marginBottom: '16px',
  position: 'relative'
});

const ActionButton = styled(Button)(({ variant: buttonVariant }) => ({
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  ...(buttonVariant === 'primary' && {
    backgroundColor: '#2196f3',
    color: 'white',
    '&:hover': { backgroundColor: '#1976d2' },
    '&:disabled': { backgroundColor: '#90caf9', color: 'white' }
  }),
  ...(buttonVariant === 'secondary' && {
    backgroundColor: '#f5f5f5',
    color: '#333',
    '&:hover': { backgroundColor: '#e0e0e0' }
  })
}));

const AddressCard = styled(Box)({
  padding: '16px',
  background: 'linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%)',
  borderRadius: '12px',
  marginBottom: '16px',
  border: '1px solid #e0e0e0'
});

const CurrentLocationButton = styled(Button)({
  position: 'absolute',
  bottom: '16px',
  right: '16px',
  zIndex: 10,
  backgroundColor: 'white',
  color: '#2196f3',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  borderRadius: '50%',
  minWidth: '48px',
  width: '48px',
  height: '48px',
  padding: 0,
  '&:hover': {
    backgroundColor: '#f5f5f5'
  }
});

const SearchContainer = styled(Box)({
  marginBottom: '16px'
});

// ============================================
// Main Component
// ============================================

const MapLocationPopup = ({
  task,
  onComplete,
  onBack,
  businessProfileData = {},
  profileId
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const placesServiceRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Location state
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Get current location from business profile
  const currentLat = businessProfileData?.latlng?.latitude ??
                     businessProfileData?.location?.latlng?.latitude ??
                     null;
  const currentLng = businessProfileData?.latlng?.longitude ??
                     businessProfileData?.location?.latlng?.longitude ??
                     null;
  const currentAddress = businessProfileData?.storefrontAddress ??
                         businessProfileData?.address ??
                         null;

  // Parse Google address components into GMB format
  const parseAddressComponents = useCallback((components, formattedAddress) => {
    const addressData = {
      formattedAddress: formattedAddress,
      regionCode: 'IN',
      languageCode: 'en',
      addressLines: []
    };

    const componentMap = {};
    components.forEach(comp => {
      comp.types.forEach(type => {
        componentMap[type] = comp.long_name;
        componentMap[`${type}_short`] = comp.short_name;
      });
    });

    // Build address lines
    const line1Parts = [];
    if (componentMap.premise) line1Parts.push(componentMap.premise);
    if (componentMap.sublocality_level_3) line1Parts.push(componentMap.sublocality_level_3);
    if (componentMap.sublocality_level_2) line1Parts.push(componentMap.sublocality_level_2);
    if (componentMap.route) line1Parts.push(componentMap.route);
    if (componentMap.street_number) line1Parts.unshift(componentMap.street_number);

    const line2Parts = [];
    if (componentMap.sublocality_level_1 || componentMap.sublocality) {
      line2Parts.push(componentMap.sublocality_level_1 || componentMap.sublocality);
    }
    if (componentMap.locality) line2Parts.push(componentMap.locality);

    if (line1Parts.length) addressData.addressLines.push(line1Parts.join(', '));
    if (line2Parts.length) addressData.addressLines.push(line2Parts.join(', '));

    // Additional fields
    addressData.locality = componentMap.locality || '';
    addressData.administrativeArea = componentMap.administrative_area_level_1_short || componentMap.administrative_area_level_1 || '';
    addressData.postalCode = componentMap.postal_code || '';

    return addressData;
  }, []);

  // Reverse geocode to get address from coordinates
  const reverseGeocode = useCallback((location) => {
    if (!geocoderRef.current) return;

    geocoderRef.current.geocode({ location }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const addressData = parseAddressComponents(
          results[0].address_components,
          results[0].formatted_address
        );
        setSelectedAddress(addressData);
        logger.log('[MapLocationPopup] Reverse geocoded address:', addressData);
      } else {
        logger.warn('[MapLocationPopup] Geocoder failed:', status);
      }
    });
  }, [parseAddressComponents]);

  // Initialize Google Maps
  useEffect(() => {
    const initMaps = async () => {
      try {
        setLoading(true);
        await loadGoogleMapsApi({ libraries: ['places', 'geocoding'] });
        setMapsLoaded(true);
        setLoading(false);
      } catch (err) {
        logger.error('[MapLocationPopup] Failed to load Google Maps:', err);
        setError('Failed to load Google Maps. Please try again.');
        setLoading(false);
      }
    };

    initMaps();
  }, []);

  // Initialize map when Maps API is loaded
  useEffect(() => {
    if (!mapsLoaded || !mapRef.current) return;

    const google = window.google;
    if (!google || !google.maps) return;

    // Default to India center if no location
    const defaultCenter = { lat: 20.5937, lng: 78.9629 };
    const initialCenter = (currentLat && currentLng)
      ? { lat: currentLat, lng: currentLng }
      : defaultCenter;
    const initialZoom = (currentLat && currentLng) ? 16 : 5;

    // Create map
    const map = new google.maps.Map(mapRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      styles: [
        {
          featureType: 'poi.business',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });
    mapInstanceRef.current = map;

    // Create geocoder
    geocoderRef.current = new google.maps.Geocoder();

    // Create marker if we have coordinates
    if (currentLat && currentLng) {
      const marker = new google.maps.Marker({
        position: initialCenter,
        map: map,
        draggable: true,
        animation: google.maps.Animation.DROP,
        title: 'Your Business Location'
      });
      markerRef.current = marker;
      setSelectedLocation(initialCenter);

      // Handle marker drag
      marker.addListener('dragend', () => {
        const position = marker.getPosition();
        const newLocation = { lat: position.lat(), lng: position.lng() };
        setSelectedLocation(newLocation);
        setHasChanges(true);
        reverseGeocode(newLocation);
      });
    }

    // Handle map click to place/move marker
    map.addListener('click', (e) => {
      const clickedLocation = { lat: e.latLng.lat(), lng: e.latLng.lng() };

      if (markerRef.current) {
        markerRef.current.setPosition(clickedLocation);
      } else {
        const marker = new google.maps.Marker({
          position: clickedLocation,
          map: map,
          draggable: true,
          animation: google.maps.Animation.DROP,
          title: 'Your Business Location'
        });
        markerRef.current = marker;

        marker.addListener('dragend', () => {
          const position = marker.getPosition();
          const newLocation = { lat: position.lat(), lng: position.lng() };
          setSelectedLocation(newLocation);
          setHasChanges(true);
          reverseGeocode(newLocation);
        });
      }

      setSelectedLocation(clickedLocation);
      setHasChanges(true);
      reverseGeocode(clickedLocation);
    });

    // Get address for current location if we have coordinates
    if (currentLat && currentLng) {
      reverseGeocode({ lat: currentLat, lng: currentLng });
    }

    return () => {
      // Cleanup
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
    };
  }, [mapsLoaded, currentLat, currentLng]);

  // Initialize Places Autocomplete separately to ensure it works with the input ref
  useEffect(() => {
    if (!mapsLoaded || !searchInputRef.current || autocompleteRef.current) return;

    const google = window.google;
    if (!google || !google.maps || !google.maps.places) return;

    try {
      const autocomplete = new google.maps.places.Autocomplete(searchInputRef.current, {
        types: ['geocode', 'establishment'],
        componentRestrictions: { country: 'in' }
      });
      autocompleteRef.current = autocomplete;

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry && place.geometry.location) {
          const newLocation = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };

          if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter(newLocation);
            mapInstanceRef.current.setZoom(17);
          }

          if (markerRef.current) {
            markerRef.current.setPosition(newLocation);
          } else if (mapInstanceRef.current) {
            const marker = new google.maps.Marker({
              position: newLocation,
              map: mapInstanceRef.current,
              draggable: true,
              animation: google.maps.Animation.DROP,
              title: 'Your Business Location'
            });
            markerRef.current = marker;

            marker.addListener('dragend', () => {
              const position = marker.getPosition();
              const newLoc = { lat: position.lat(), lng: position.lng() };
              setSelectedLocation(newLoc);
              setHasChanges(true);
              reverseGeocode(newLoc);
            });
          }

          setSelectedLocation(newLocation);
          setHasChanges(true);
          setSearchValue(place.formatted_address || place.name || '');

          // Parse address from place
          if (place.address_components) {
            const addressData = parseAddressComponents(place.address_components, place.formatted_address);
            setSelectedAddress(addressData);
          } else {
            reverseGeocode(newLocation);
          }
        }
      });

      logger.log('[MapLocationPopup] Places Autocomplete initialized');
    } catch (err) {
      logger.error('[MapLocationPopup] Failed to initialize Places Autocomplete:', err);
    }
  }, [mapsLoaded, reverseGeocode, parseAddressComponents]);

  // Use current GPS location
  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setFetchingLocation(true);
    setError('');

    const onSuccess = (position) => {
      const newLocation = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };

      if (mapInstanceRef.current) {
        mapInstanceRef.current.setCenter(newLocation);
        mapInstanceRef.current.setZoom(17);
      }

      if (markerRef.current) {
        markerRef.current.setPosition(newLocation);
      } else if (mapInstanceRef.current) {
        const marker = new window.google.maps.Marker({
          position: newLocation,
          map: mapInstanceRef.current,
          draggable: true,
          animation: window.google.maps.Animation.DROP,
          title: 'Your Business Location'
        });
        markerRef.current = marker;

        marker.addListener('dragend', () => {
          const pos = marker.getPosition();
          const newLoc = { lat: pos.lat(), lng: pos.lng() };
          setSelectedLocation(newLoc);
          setHasChanges(true);
          reverseGeocode(newLoc);
        });
      }

      setSelectedLocation(newLocation);
      setHasChanges(true);
      reverseGeocode(newLocation);
      setFetchingLocation(false);
    };

    const onError = (err) => {
      logger.error('[MapLocationPopup] Geolocation error:', err);

      // If high accuracy timed out, try with low accuracy
      if (err.code === err.TIMEOUT) {
        logger.log('[MapLocationPopup] Retrying with low accuracy...');
        navigator.geolocation.getCurrentPosition(
          onSuccess,
          (retryErr) => {
            let errorMessage = 'Unable to get your location. ';
            switch(retryErr.code) {
              case retryErr.PERMISSION_DENIED:
                errorMessage += 'Please allow location access in your browser settings.';
                break;
              case retryErr.POSITION_UNAVAILABLE:
                errorMessage += 'Location information is unavailable.';
                break;
              case retryErr.TIMEOUT:
                errorMessage += 'Location request timed out. Please search for your address instead.';
                break;
              default:
                errorMessage += 'Please try searching for your address instead.';
            }
            setError(errorMessage);
            setFetchingLocation(false);
          },
          { enableHighAccuracy: false, timeout: 30000, maximumAge: 60000 }
        );
        return;
      }

      let errorMessage = 'Unable to get your location. ';
      switch(err.code) {
        case err.PERMISSION_DENIED:
          errorMessage += 'Please allow location access in your browser settings.';
          break;
        case err.POSITION_UNAVAILABLE:
          errorMessage += 'Location information is unavailable.';
          break;
        case err.TIMEOUT:
          errorMessage += 'Location request timed out. Please search for your address instead.';
          break;
        default:
          errorMessage += 'Please try searching for your address instead.';
      }
      setError(errorMessage);
      setFetchingLocation(false);
    };

    // First try with high accuracy, shorter timeout
    navigator.geolocation.getCurrentPosition(
      onSuccess,
      onError,
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [reverseGeocode]);

  // Submit location update to GMB
  const handleSubmit = async () => {
    if (!selectedLocation || !selectedAddress) {
      setError('Please select a location on the map first');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // Get location ID
      const locationId = profileId ||
                        businessProfileData?.name?.split('/').pop() ||
                        businessProfileData?.locationId;

      if (!locationId) {
        throw new Error('Location ID not found');
      }

      // Prepare update payload
      const updatePayload = {
        storefrontAddress: {
          regionCode: selectedAddress.regionCode || 'IN',
          languageCode: selectedAddress.languageCode || 'en',
          addressLines: selectedAddress.addressLines || [selectedAddress.formattedAddress],
          locality: selectedAddress.locality || '',
          administrativeArea: selectedAddress.administrativeArea || '',
          postalCode: selectedAddress.postalCode || ''
        },
        // Include latlng in body for validation but it won't be in updateMask
        latlng: {
          latitude: selectedLocation.lat,
          longitude: selectedLocation.lng
        }
      };

      logger.log('[MapLocationPopup] Updating location:', updatePayload);

      // Call GMB Service to update
      await GMBService.updateBusinessProfile(locationId, updatePayload);

      setSuccess(true);
      logger.log('[MapLocationPopup] Location updated successfully');

      // Wait a moment then complete
      setTimeout(() => {
        if (onComplete) {
          onComplete({
            completed: true,
            taskId: task?.id,
            updatedLocation: selectedLocation,
            updatedAddress: selectedAddress
          });
        }
      }, 1500);

    } catch (err) {
      logger.error('[MapLocationPopup] Update failed:', err);

      // Handle specific error messages
      let errorMsg = 'Failed to update location. Please try again.';
      if (err.message?.includes('LAT_LNG_UPDATES_NOT_PERMITTED')) {
        errorMsg = 'The address has been updated. Google will verify and update the map pin location.';
        setSuccess(true);
        setTimeout(() => {
          if (onComplete) {
            onComplete({ completed: true, taskId: task?.id });
          }
        }, 2000);
        return;
      }

      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Success State
  if (success) {
    return (
      <Box>
        <HeaderContainer>
          <IconContainer>
            <CheckCircle sx={{ fontSize: 36 }} />
          </IconContainer>
          <Typography variant="h5" fontWeight={700}>
            Location Updated!
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
            Your business location has been updated successfully
          </Typography>
        </HeaderContainer>
        <ContentContainer>
          <Alert severity="success" sx={{ mb: 2 }}>
            Your map location has been submitted to Google. It may take a few minutes for changes to reflect on Google Maps.
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ActionButton
              variant="primary"
              onClick={() => onComplete?.({ completed: true, taskId: task?.id })}
            >
              Done
            </ActionButton>
          </Box>
        </ContentContainer>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <HeaderContainer>
        <Box sx={{ position: 'absolute', left: 16, top: 16 }}>
          <IconButton onClick={onBack} sx={{ color: 'white' }}>
            <ArrowBack />
          </IconButton>
        </Box>
        <IconContainer>
          <LocationOn sx={{ fontSize: 36 }} />
        </IconContainer>
        <Typography variant="h5" fontWeight={700}>
          Fix Your Map Location
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
          Click on the map or search to set your exact business location
        </Typography>
      </HeaderContainer>

      {/* Content */}
      <ContentContainer>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Search Box */}
        <SearchContainer>
          <TextField
            fullWidth
            placeholder="Search for your business address..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            inputProps={{
              ref: searchInputRef,
              autoComplete: 'off'
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px'
              }
            }}
          />
        </SearchContainer>

        {/* Map */}
        <MapContainer>
          {loading ? (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              background: '#f5f5f5'
            }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
              <CurrentLocationButton
                onClick={handleUseCurrentLocation}
                disabled={fetchingLocation}
                title="Use my current location"
              >
                {fetchingLocation ? <CircularProgress size={24} /> : <MyLocation />}
              </CurrentLocationButton>
            </>
          )}
        </MapContainer>

        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>How to set your location:</strong>
            <br />
            1. Search for your address in the search box above
            <br />
            2. Or click directly on the map to place a pin
            <br />
            3. Drag the pin to fine-tune the exact position
          </Typography>
        </Alert>

        {/* Selected Address Card */}
        {selectedAddress && (
          <AddressCard>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Place color="primary" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} color="primary">
                  Selected Location
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedAddress.formattedAddress || selectedAddress.addressLines?.join(', ')}
                </Typography>
                {selectedLocation && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Coordinates: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                  </Typography>
                )}
              </Box>
            </Box>
          </AddressCard>
        )}

        {/* Current Address (if different) */}
        {currentAddress && !hasChanges && (
          <AddressCard sx={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #f3f9ff 100%)', borderColor: '#90caf9' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <MapIcon color="info" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} color="info.main">
                  Current Address
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {currentAddress.addressLines?.join(', ') ||
                   `${currentAddress.locality || ''} ${currentAddress.administrativeArea || ''} ${currentAddress.postalCode || ''}`.trim()}
                </Typography>
              </Box>
            </Box>
          </AddressCard>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <ActionButton variant="secondary" onClick={onBack}>
            Cancel
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting || !selectedLocation}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircle />}
          >
            {submitting ? 'Updating...' : 'Update Location'}
          </ActionButton>
        </Box>
      </ContentContainer>
    </Box>
  );
};

export default MapLocationPopup;
